import classNames from "classnames";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { BsArchive } from "react-icons/bs";
import { FaBoxArchive } from "react-icons/fa6";
import { HiXMark } from "react-icons/hi2";
import { MultiValue } from "react-select";

import Button from "../../../components/button/Button";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import CustomSelect from "../../../components/select/Select";
import { HomeFilter } from "../../../factories/works/models/home-enums";
import { WorkModel } from "../../../factories/works/models/work.model";
import { useHomeSearchParams } from "../../../hooks/useHomeSearchParams";
import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetStatusesQuery } from "../../../slices/StatusesSlice";
import {
  useArchiveOrUnarchiveMutation,
  useGetFirmsWorksCountsQuery,
  useGetHomeWorksQuery,
  useUpdateMultipleStatusesMutation,
} from "../../../slices/WorkSlice";
import { useGetWorkTypesQuery } from "../../../slices/WorkTypeSlice";
import { SelectOptionType } from "../../../types/types";
import { WorkList } from "../../works/components/WorkList";
import { AddWorkModal } from "../../works/modals/AddWork.modal";
import { HomeStats } from "./HomeStats";

type HomeWorksPageProps = {
  title: string;
  isMyWork: boolean;
};

type HomePageFilters = {
  workStatuses: SelectOptionType[];
  worktypeIds: SelectOptionType[];
};

const EMPTY_FILTERS: HomePageFilters = {
  workStatuses: [],
  worktypeIds: [],
};

const BULK_STATUSES = [
  { color: "#93AAFD", name: "Ready" },
  { color: "#FFD80B", name: "In Progress" },
  { color: "#F15252", name: "Waiting" },
  { color: "#47DE96", name: "Completed" },
] as const;

export const HomeWorksPage: FC<HomeWorksPageProps> = ({ title, isMyWork }) => {
  const userId = useAppSelector((state) => state.appReducer.user?.id);
  const orgId = getLinkedFirm()?.orgId;

  const { activeTab, activeFilter, page, sortBy, sortOrder, setPage, setSort } =
    useHomeSearchParams();

  const [selectedWorks, setSelectedWorks] = useState<WorkModel[]>([]);
  const [filters, setFilters] = useState<HomePageFilters>(EMPTY_FILTERS);
  const [searchWorks, setSearchWorks] = useState("");
  const [isAddWorkOpen, setIsAddWorkOpen] = useState(false);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);

  const [archiveOrUnarchive, { isLoading: isArchiveLoading }] =
    useArchiveOrUnarchiveMutation();

  const [updateMultipleStatuses, { isLoading: isStatusUpdateLoading }] =
    useUpdateMultipleStatusesMutation();

  const canLoad = Boolean(orgId && userId);

  const statusLabels = useMemo(
    () => filters.workStatuses.map((item) => item.label),
    [filters.workStatuses],
  );

  const workTypeIds = useMemo(
    () => filters.worktypeIds.map((item) => String(item.value)),
    [filters.worktypeIds],
  );

  const dueDates =
    activeFilter === HomeFilter.OverDue ? HomeFilter.OverDue : activeTab;

  const homeWorksArgs = useMemo(
    () => ({
      currentUserId: userId || "",
      pageNumber: page,
      pageSize: 20,
      sortBy,
      sortOrder,
      dueDates,
      status: statusLabels,
      orgId: orgId || "",
      worktypeIds: workTypeIds,
      ...(isMyWork ? { assignedUserId: userId || "" } : {}),
    }),
    [
      userId,
      page,
      sortBy,
      sortOrder,
      dueDates,
      statusLabels,
      orgId,
      workTypeIds,
      isMyWork,
    ],
  );

  const countsArgs = useMemo(
    () => ({
      orgId: orgId || "",
      userId: userId || "",
      isMyWork,
    }),
    [orgId, userId, isMyWork],
  );

  const {
    data: counts,
    isLoading: isLoadingCounts,
    isError: isCountsError,
  } = useGetFirmsWorksCountsQuery(countsArgs, {
    skip: !canLoad,
  });

  const { data: works = [], isLoading: isLoadingWorks } = useGetHomeWorksQuery(
    homeWorksArgs,
    {
      skip: !canLoad,
    },
  );

  const { data: workTypes, isLoading: isLoadingWorkTypes } =
    useGetWorkTypesQuery(
      { orgId: orgId || "" },
      {
        skip: !orgId,
      },
    );

  const { data: statuses, isLoading: isLoadingStatuses } = useGetStatusesQuery(
    { orgId: orgId || "" },
    {
      skip: !orgId,
    },
  );

  const totalRecords = works?.[0]?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / 20));

  const workTypeOptions = useMemo<SelectOptionType[]>(() => {
    return (workTypes ?? []).map((item) => ({
      label: item.name,
      value: item.id,
    }));
  }, [workTypes]);

  const statusOptions = useMemo<SelectOptionType[]>(() => {
    return (statuses ?? []).flatMap((item) => [
      {
        label: item.name,
        value: item.id,
        color: item.color,
        isBold: true,
      },
      ...((item.workTypeSubStatuses ?? []).map((subItem) => ({
        label: subItem.subStatus,
        value: subItem.id,
        color: item.color,
      })) as SelectOptionType[]),
    ]);
  }, [statuses]);

  const hasSelectedWorks = selectedWorks.length > 0;
  const hasActiveFilters =
    filters.workStatuses.length > 0 || filters.worktypeIds.length > 0;

  useEffect(() => {
    if (!hasSelectedWorks && isStatusMenuOpen) {
      setIsStatusMenuOpen(false);
    }
  }, [hasSelectedWorks, isStatusMenuOpen]);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, [setPage]);

  const handleWorkTypesChange = useCallback(
    (newValue: MultiValue<SelectOptionType> | SelectOptionType) => {
      const value = Array.isArray(newValue)
        ? newValue
        : newValue
        ? [newValue]
        : [];

      setFilters((prev) => ({
        ...prev,
        worktypeIds: value,
      }));

      setPage(1);
    },
    [setPage],
  );

  const handleStatusesChange = useCallback(
    (newValue: MultiValue<SelectOptionType> | SelectOptionType) => {
      const value = Array.isArray(newValue)
        ? newValue
        : newValue
        ? [newValue]
        : [];

      setFilters((prev) => ({
        ...prev,
        workStatuses: value,
      }));

      setPage(1);
    },
    [setPage],
  );

  const handleBulkStatusChange = useCallback(
    async (status: string) => {
      if (!hasSelectedWorks) return;

      await updateMultipleStatuses({
        body: selectedWorks.map((item) => item.workId),
        status,
      }).unwrap();

      setSelectedWorks([]);
      setIsStatusMenuOpen(false);
    },
    [hasSelectedWorks, selectedWorks, updateMultipleStatuses],
  );

  const handleArchiveConfirm = useCallback(async () => {
    if (!hasSelectedWorks) return;

    await archiveOrUnarchive({
      body: selectedWorks.map((item) => item.workId),
      archiveWork: true,
    }).unwrap();

    setSelectedWorks([]);
    setIsArchiveConfirmOpen(false);
  }, [archiveOrUnarchive, hasSelectedWorks, selectedWorks]);

  if (!canLoad) {
    return <LottieLoading />;
  }

  if (isCountsError) {
    return (
      <div
        className={"flex h-[60vh] items-center justify-center text-gray-500"}>
        Failed to load works dashboard.
      </div>
    );
  }

  return (
    <>
      <div className={"grid h-full w-full grid-rows-[max-content_1fr] gap-6"}>
        <HomeStats
          isLoading={isLoadingCounts}
          isError={isCountsError}
          thisWeek={counts?.thisWeek?.total || 0}
          nextWeek={counts?.nextWeek?.total || 0}
          later={counts?.later?.total || 0}
          allOpen={counts?.allOpen?.total || 0}
          completed={counts?.completed || 0}
        />

        <WorkList
          works={works}
          statusFilter={statusLabels}
          archiveWorks={selectedWorks}
          setArchiveWorks={setSelectedWorks}
          isLoading={isLoadingWorks}
          hasPages
          page={page}
          totalPages={totalPages}
          total={totalRecords}
          pageSize={20}
          onPageChange={setPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={setSort}
          {...(isMyWork ? { myWorkHome: true, filter: dueDates } : {})}>
          <div
            className={
              "grid grid-cols-[1fr_max-content] items-start gap-2 px-8 pt-6"
            }>
            <div className={"flex items-center gap-4"}>
              <div className={"font-medium"}>{title}</div>

              <div className={"flex items-end gap-4 pb-4"}>
                <div className={"w-[150px] min-w-[100px]"}>
                  <div className={"text-sm font-semibold text-gray-400"}>
                    Work Type
                  </div>
                  <CustomSelect
                    options={workTypeOptions}
                    value={filters.worktypeIds}
                    onChange={handleWorkTypesChange}
                    inputValue={searchWorks}
                    onChangeInput={setSearchWorks}
                    isMulti
                    isLoading={isLoadingWorkTypes}
                    withMenu
                    placeholder={"All"}
                    selectStyle={{
                      fitChips: true,
                      small: true,
                      width: "250px",
                      workTable: true,
                    }}
                  />
                </div>

                <div className={"w-[150px] min-w-[100px]"}>
                  <div className={"text-sm font-semibold text-gray-400"}>
                    Status
                  </div>
                  <CustomSelect
                    options={statusOptions}
                    value={filters.workStatuses}
                    onChange={handleStatusesChange}
                    withMenu
                    isLoading={isLoadingStatuses}
                    placeholder={"All"}
                    isMulti
                    selectStyle={{
                      fitChips: true,
                      small: true,
                      width: "250px",
                      workTable: true,
                    }}
                  />
                </div>

                {hasActiveFilters && (
                  <div className={"mt-4 flex items-center"}>
                    <Button
                      label={"Clear"}
                      onClick={clearFilters}
                      colorType={"ghost"}
                      icon={<HiXMark size={20} />}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={"flex items-center gap-4"}>
              <div className={"relative"}>
                <button
                  type={"button"}
                  disabled={!hasSelectedWorks || isStatusUpdateLoading}
                  className={classNames(
                    hasSelectedWorks ? "text-purple" : "text-gray-300",
                    "w-[120px] text-left text-base font-semibold disabled:cursor-not-allowed",
                  )}
                  onClick={() => setIsStatusMenuOpen((prev) => !prev)}>
                  Change status
                </button>

                {isStatusMenuOpen && hasSelectedWorks && (
                  <div
                    className={
                      "absolute top-[30px] z-10 flex w-[150px] flex-col gap-1 rounded-[8px] border border-black border-opacity-[0.2] bg-white"
                    }>
                    {BULK_STATUSES.map((status) => (
                      <button
                        key={status.name}
                        type={"button"}
                        className={
                          "flex cursor-pointer items-center gap-2 px-4 py-2 text-left"
                        }
                        onClick={() => handleBulkStatusChange(status.name)}>
                        <div
                          style={{ backgroundColor: status.color }}
                          className={"h-2.5 w-2.5 rounded-[50%]"}
                        />
                        <div className={"text-sm font-semibold"}>
                          {status.name}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                type={"button"}
                disabled={!hasSelectedWorks || isArchiveLoading}
                onClick={() => setIsArchiveConfirmOpen(true)}
                className={"disabled:cursor-not-allowed disabled:opacity-40"}>
                <BsArchive
                  data-tooltip-id={"tooltip-archive"}
                  color={"#7c66f0"}
                  size={"20px"}
                  className={"flex items-center"}
                />
              </button>

              <Button
                buttonType={"button"}
                label={"Add work"}
                onClick={() => {
                  setSelectedWorks([]);
                  setIsAddWorkOpen(true);
                }}
              />
            </div>
          </div>
        </WorkList>
      </div>

      {isAddWorkOpen && (
        <AddWorkModal
          isOpen={isAddWorkOpen}
          onClose={() => setIsAddWorkOpen(false)}
        />
      )}

      {isArchiveConfirmOpen && (
        <SettingsConfirmModal
          onClose={() => setIsArchiveConfirmOpen(false)}
          onConfirm={handleArchiveConfirm}
          icon={<FaBoxArchive size={"18px"} />}
          title={
            selectedWorks.length > 1
              ? "Archive the selected work items?"
              : `Archive "${selectedWorks?.[0]?.workName}" work?`
          }
          description={
            selectedWorks.length > 1
              ? 'Are you sure you want to archive these works? They will be moved to the "Archived" section. You can reactivate them at any time.'
              : 'Are you sure you want to archive this work? It will be moved to the "Archived" section. You can reactivate it at any time.'
          }
          confirmText={"Continue"}
          variant={"primary"}
        />
      )}
    </>
  );
};
